
import { useState, useEffect } from "react"
import { Smartphone, Monitor, RefreshCw } from "lucide-react"
import DocumentUploader from "./document-uploader"
import { useDeviceDetection } from "./use-device-detection"
import { useDocumentSync } from "./use-document-sync"
import { useLocation } from "react-router-dom"

export default function PatientIntakeForm() {
  const [formData, setFormData] = useState(
    {
      fullName: "",
      dob: "",
      address: "",
      phone: "",
      email: "",
      gender: "",
      ssn: "",
      insuranceCompanyName: "",
      insuranceIDNumber: "",
      groupNumber: "",
      policyholderName: "",
      policyholderDob: "",
      relationshipToPolicyholder: "",
      insuranceEffectiveDate: "",
      planType: "",
      hasSecondaryInsurance: false,
      secondaryInsuranceCompanyName: "",
      secondaryInsuranceIDNumber: "",
      secondaryGroupNumber: "",
      secondaryPolicyholderName: "",
      secondaryPolicyholderDob: "",
      secondaryRelationshipToPolicyholder: "",
      secondaryInsuranceEffectiveDate: "",
      secondaryPlanType: "",
      dlNumber: "",
      dlState: "",
      dlIssueDate: "",
      dlExpirationDate: "",
    }
  )
  const generateSessionId = () => {
    const array = new Uint32Array(4);
    window.crypto.getRandomValues(array);
    return Array.from(array, dec => dec.toString(36)).join("-");
  };
  const [message, setMessage] = useState(null)
  const [sessionId, setSessionId] = useState(() => generateSessionId())
  const [submitting, setSubmitting] = useState(false);

  const { isMobile, isTablet } = useDeviceDetection()
  const { uploadedDocuments, setUploadedDocuments, addDocument, getDocumentsByType, removeDocument } = useDocumentSync(sessionId)

  const isMobileOrTablet = isMobile || isTablet

  const resetFormData = () => {
    setUploadedDocuments([])
    setFormData({
      fullName: "",
      dob: "",
      address: "",
      phone: "",
      email: "",
      gender: "",
      ssn: "",
      insuranceCompanyName: "",
      insuranceIDNumber: "",
      groupNumber: "",
      policyholderName: "",
      policyholderDob: "",
      relationshipToPolicyholder: "",
      insuranceEffectiveDate: "",
      planType: "",
      hasSecondaryInsurance: false,
      secondaryInsuranceCompanyName: "",
      secondaryInsuranceIDNumber: "",
      secondaryGroupNumber: "",
      secondaryPolicyholderName: "",
      secondaryPolicyholderDob: "",
      secondaryRelationshipToPolicyholder: "",
      secondaryInsuranceEffectiveDate: "",
      secondaryPlanType: "",
      dlNumber: "",
      dlState: "",
      dlIssueDate: "",
      dlExpirationDate: "",
    })
    setSessionId(() => generateSessionId())
  }

  const showMessage = (text, type) => {
    setMessage({ text, type })
    setTimeout(() => setMessage(null), 5000)
  }

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleDocumentUpload = (file, documentType) => {
    addDocument(file, documentType)
  }

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Convert each uploaded document to base64
      const documentsWithBase64 = await Promise.all(
        uploadedDocuments.map(async ({ id, documentType, file, uploadedAt }) => ({
          id,
          documentType,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          uploadedAt,
          fileData: await readFileAsBase64(file),
        }))
      );

      const submissionData = {
        ...formData,
        uploadedDocuments: documentsWithBase64,
        sessionId,
      };

      const response = await fetch(
        `${window.ajaxLink}cloud_dashboard/submitNewPatientIntake`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(submissionData),
        }
      );

      if (!response.ok) {
        throw new Error(`Server responded with status ${response.status}`);
      }

      showMessage(
        `Form submitted successfully!`,
        "success"
      );
      resetFormData();
    } catch (error) {
      console.error("Error submitting form:", error);
      showMessage("Error submitting form. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-[1600px] mx-auto">
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#166273] to-[#166273]/90 px-6 py-8 sm:px-8">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-white mb-4">New Patient Intake: Eligibility & Claims Information</h1>
              <p className="text-blue-100 max-w-2xl mx-auto">
                Please provide your information below. This helps us verify your insurance and ensure smooth billing for your physical therapy services.
              </p>
            </div>
          </div>

          <div className="px-6 py-8 sm:px-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Information Section */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-xl font-semibold text-gray-900">1. Personal Information</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Legal Name (as on ID) *
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        value={formData.fullName}
                        onChange={(e) => handleInputChange("fullName", e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="dob" className="block text-sm font-medium text-gray-700 mb-2">
                        Date of Birth *
                      </label>
                      <input
                        type="date"
                        id="dob"
                        value={formData.dob}
                        onChange={(e) => handleInputChange("dob", e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                        Current Address *
                      </label>
                      <input
                        type="text"
                        id="address"
                        value={formData.address}
                        onChange={(e) => handleInputChange("address", e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                        Gender *
                      </label>
                      <select
                        id="gender"
                        value={formData.gender}
                        onChange={(e) => handleInputChange("gender", e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      >
                        <option value="">Select...</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                        <option value="prefer_not_to_say">Prefer not to say</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="ssn" className="block text-sm font-medium text-gray-700 mb-2">
                        Social Security Number (Optional)
                      </label>
                      <input
                        type="text"
                        id="ssn"
                        value={formData.ssn}
                        onChange={(e) => handleInputChange("ssn", e.target.value)}
                        placeholder="Optional"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Primary Insurance Section */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-xl font-semibold text-gray-900">2. Insurance Information (Primary)</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Provide details for your primary insurance. You can upload or scan your insurance card.
                  </p>
                </div>
                <div className="p-6 space-y-6">
                  <DocumentUploader
                    documentType="Primary Insurance Card"
                    sessionId={sessionId}
                    isMobileOrTablet={isMobileOrTablet}
                    onUpload={handleDocumentUpload}
                    allowMultiple={true}
                    description="Upload both front and back of your insurance card"
                    uploadedDocuments={getDocumentsByType("Primary Insurance Card")}
                    removeDocument={removeDocument}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <p className="md:col-span-2 text-sm text-gray-600 mb-2">
                      Alternatively, please enter your insurance details manually:
                    </p>
                    <div>
                      <label htmlFor="insuranceCompanyName" className="block text-sm font-medium text-gray-700 mb-1">
                        Insurance Company Name *
                      </label>
                      <input
                        type="text"
                        required
                        id="insuranceCompanyName"
                        name="insuranceCompanyName"
                        value={formData.insuranceCompanyName}
                        onChange={(e) => handleInputChange("insuranceCompanyName", e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="insuranceIDNumber" className="block text-sm font-medium text-gray-700 mb-1">
                        Insurance ID Number *
                      </label>
                      <input
                        type="text"
                        required
                        id="insuranceIDNumber"
                        name="insuranceIDNumber"
                        value={formData.insuranceIDNumber}
                        onChange={(e) => handleInputChange("insuranceIDNumber", e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="groupNumber" className="block text-sm font-medium text-gray-700 mb-1">
                        Group Number (if applicable)
                      </label>
                      <input
                        type="text"
                        id="groupNumber"
                        name="groupNumber"
                        value={formData.groupNumber}
                        onChange={(e) => handleInputChange("groupNumber", e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="policyholderName" className="block text-sm font-medium text-gray-700 mb-1">
                        Policyholder Name (if different)
                      </label>
                      <input
                        type="text"
                        id="policyholderName"
                        name="policyholderName"
                        value={formData.policyholderName}
                        onChange={(e) => handleInputChange("policyholderName", e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="policyholderDob" className="block text-sm font-medium text-gray-700 mb-1">
                        Policyholder Date of Birth (if different)
                      </label>
                      <input
                        type="date"
                        id="policyholderDob"
                        name="policyholderDob"
                        value={formData.policyholderDob}
                        onChange={(e) => handleInputChange("policyholderDob", e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="relationshipToPolicyholder" className="block text-sm font-medium text-gray-700 mb-1">
                        Relationship to Policyholder *
                      </label>
                      <select
                        required
                        id="relationshipToPolicyholder"
                        name="relationshipToPolicyholder"
                        value={formData.relationshipToPolicyholder}
                        onChange={(e) => handleInputChange("relationshipToPolicyholder", e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select...</option>
                        <option value="self">Self</option>
                        <option value="spouse">Spouse</option>
                        <option value="child">Child</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="insuranceEffectiveDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Effective Date of Coverage *
                      </label>
                      <input
                        type="date"
                        required
                        id="insuranceEffectiveDate"
                        name="insuranceEffectiveDate"
                        value={formData.insuranceEffectiveDate}
                        onChange={(e) => handleInputChange("insuranceEffectiveDate", e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="planType" className="block text-sm font-medium text-gray-700 mb-1">
                        Plan Type (e.g., PPO, HMO) *
                      </label>
                      <input
                        type="text"
                        required
                        id="planType"
                        name="planType"
                        value={formData.planType}
                        onChange={(e) => handleInputChange("planType", e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., PPO, HMO"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="hasSecondaryInsurance"
                      checked={formData.hasSecondaryInsurance}
                      onChange={(e) => handleInputChange("hasSecondaryInsurance", e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="hasSecondaryInsurance" className="ml-2 block text-sm text-gray-900">
                      I have secondary insurance
                    </label>
                  </div>

                  {/* Secondary Insurance Section */}
                  {formData.hasSecondaryInsurance && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Secondary Insurance Information</h3>

                      <div className="mb-6">
                        <DocumentUploader
                          documentType="Secondary Insurance Card"
                          sessionId={sessionId}
                          isMobileOrTablet={isMobileOrTablet}
                          onUpload={handleDocumentUpload}
                          allowMultiple={true}
                          description="Upload both front and back of your secondary insurance card"
                          uploadedDocuments={getDocumentsByType("Secondary Insurance Card")}
                          removeDocument={removeDocument}
                        />
                      </div>


                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <p className="md:col-span-2 text-sm text-gray-600 mb-2">
                          Alternatively, please enter your secondary insurance details manually:
                        </p>
                        <div>
                          <label
                            htmlFor="secondaryInsuranceCompanyName"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Secondary Insurance Company Name *
                          </label>
                          <input
                            type="text"
                            required
                            id="secondaryInsuranceCompanyName"
                            name="secondaryInsuranceCompanyName"
                            value={formData.secondaryInsuranceCompanyName}
                            onChange={(e) => handleInputChange("secondaryInsuranceCompanyName", e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="secondaryInsuranceIDNumber"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Secondary Insurance ID Number *
                          </label>
                          <input
                            type="text"
                            required
                            id="secondaryInsuranceIDNumber"
                            name="secondaryInsuranceIDNumber"
                            value={formData.secondaryInsuranceIDNumber}
                            onChange={(e) => handleInputChange("secondaryInsuranceIDNumber", e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label htmlFor="secondaryGroupNumber" className="block text-sm font-medium text-gray-700 mb-1">
                            Secondary Group Number (if applicable)
                          </label>
                          <input
                            type="text"
                            id="secondaryGroupNumber"
                            name="secondaryGroupNumber"
                            value={formData.secondaryGroupNumber}
                            onChange={(e) => handleInputChange("secondaryGroupNumber", e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label htmlFor="secondaryPolicyholderName" className="block text-sm font-medium text-gray-700 mb-1">
                            Secondary Policyholder Name (if different)
                          </label>
                          <input
                            type="text"
                            id="secondaryPolicyholderName"
                            name="secondaryPolicyholderName"
                            value={formData.secondaryPolicyholderName}
                            onChange={(e) => handleInputChange("secondaryPolicyholderName", e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label htmlFor="secondaryPolicyholderDob" className="block text-sm font-medium text-gray-700 mb-1">
                            Secondary Policyholder Date of Birth (if different)
                          </label>
                          <input
                            type="date"
                            id="secondaryPolicyholderDob"
                            name="secondaryPolicyholderDob"
                            value={formData.secondaryPolicyholderDob}
                            onChange={(e) => handleInputChange("secondaryPolicyholderDob", e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="secondaryRelationshipToPolicyholder"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Secondary Relationship to Policyholder *
                          </label>
                          <select
                            id="secondaryRelationshipToPolicyholder"
                            required
                            name="secondaryRelationshipToPolicyholder"
                            value={formData.secondaryRelationshipToPolicyholder}
                            onChange={(e) => handleInputChange("secondaryRelationshipToPolicyholder", e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select...</option>
                            <option value="self">Self</option>
                            <option value="spouse">Spouse</option>
                            <option value="child">Child</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label
                            htmlFor="secondaryInsuranceEffectiveDate"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Secondary Effective Date of Coverage *
                          </label>
                          <input
                            type="date"
                            required
                            id="secondaryInsuranceEffectiveDate"
                            name="secondaryInsuranceEffectiveDate"
                            value={formData.secondaryInsuranceEffectiveDate}
                            onChange={(e) => handleInputChange("secondaryInsuranceEffectiveDate", e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label htmlFor="secondaryPlanType" className="block text-sm font-medium text-gray-700 mb-1">
                            Secondary Plan Type (e.g., PPO, HMO) *
                          </label>
                          <input
                            type="text"
                            required
                            id="secondaryPlanType"
                            name="secondaryPlanType"
                            value={formData.secondaryPlanType}
                            onChange={(e) => handleInputChange("secondaryPlanType", e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., PPO, HMO"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Driver's License Section */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-xl font-semibold text-gray-900">3. Driver's License Information</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Please provide your Driver's License details. You can upload or scan it.
                  </p>
                </div>
                <div className="p-6 space-y-6">
                  <DocumentUploader
                    documentType="Driver's License"
                    sessionId={sessionId}
                    isMobileOrTablet={isMobileOrTablet}
                    onUpload={handleDocumentUpload}
                    allowMultiple={true}
                    description="Upload both front and back of your driver's license"
                    uploadedDocuments={getDocumentsByType("Driver's License")}
                    removeDocument={removeDocument}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <p className="md:col-span-2 text-sm text-gray-600 mb-2">
                      Alternatively, please enter your Driver's License details manually:
                    </p>
                    <div>
                      <label htmlFor="dlNumber" className="block text-sm font-medium text-gray-700 mb-1">
                        Driver's License Number *
                      </label>
                      <input
                        type="text"
                        required
                        id="dlNumber"
                        name="dlNumber"
                        value={formData.dlNumber}
                        onChange={(e) => handleInputChange("dlNumber", e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="dlState" className="block text-sm font-medium text-gray-700 mb-1">
                        Issuing State *
                      </label>
                      <input
                        type="text"
                        required
                        id="dlState"
                        name="dlState"
                        value={formData.dlState}
                        onChange={(e) => handleInputChange("dlState", e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="dlIssueDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Issue Date *
                      </label>
                      <input
                        type="date"
                        required
                        id="dlIssueDate"
                        name="dlIssueDate"
                        value={formData.dlIssueDate}
                        onChange={(e) => handleInputChange("dlIssueDate", e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="dlExpirationDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Expiration Date *
                      </label>
                      <input
                        type="date"
                        required
                        id="dlExpirationDate"
                        name="dlExpirationDate"
                        value={formData.dlExpirationDate}
                        onChange={(e) => handleInputChange("dlExpirationDate", e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>


              {/* Alert Message */}
              {message && (
                <div
                  className={`mb-6 p-4 rounded-lg border ${message.type === "error"
                    ? "bg-red-50 border-red-200 text-red-700"
                    : message.type === "success"
                      ? "bg-green-50 border-green-200 text-green-700"
                      : "bg-blue-50 border-blue-200 text-blue-700"
                    }`}
                >
                  <div className="flex justify-between items-start">
                    <p>{message.text}</p>
                    <button onClick={() => setMessage(null)} className="ml-4 text-gray-400 hover:text-gray-600">
                      ×
                    </button>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#2A6F7F] hover:bg-[#2A6F7F]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors shadow-lg"
                >
                  {submitting && (
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      style={{ marginRight: '10px' }}
                    >
                      <circle
                        className="opacity-25"
                        cx="12" cy="12" r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                  )}
                  {submitting ? "Submitting..." : "Submit Information"}
                </button>

              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
